import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { clinicInfo, services } from '../mock';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About', about: true },
  { to: '/services', label: 'Services', children: true },
  { to: '/contact', label: 'Contact' },
  { to: '/blog', label: 'Blog' },
  { to: '/faq', label: 'FAQ' }
];

const ABOUT_LINKS = [
  { to: '/about/our-story', label: 'Our Story' },
  { to: '/team', label: 'Our Team' }
];

const LOGO_URL = '/img/logo-wordmark.webp';

export default function Navbar({ onBookClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const closeTimer = useRef(null);
  const aboutCloseTimer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setAboutOpen(false);
    setMobileServicesOpen(false);
    setMobileAboutOpen(false);
  }, [pathname]);

  const openServices = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };
  const scheduleCloseServices = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setServicesOpen(false), 120);
  };
  const openAbout = () => {
    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
    setAboutOpen(true);
  };
  const scheduleCloseAbout = () => {
    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
    aboutCloseTimer.current = setTimeout(() => setAboutOpen(false), 120);
  };

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  return (
    <header role="banner" className="fixed top-0 left-0 right-0 z-50">
      {/* Top info bar */}
      <div className={`transition-all duration-500 bg-[#0B0B0B] text-[#C8A97E] border-b border-[#C8A97E]/15 ${scrolled ? 'h-0 overflow-hidden opacity-0' : 'h-9 opacity-100'}`}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-9 flex items-center justify-between text-[11px] tracking-[0.18em]">
          <a href="tel:+15197414141" className="flex items-center gap-2 hover:text-[#FAF6F0] transition-colors">
            <Phone size={12} strokeWidth={1.5} /><span>519.741.4141</span>
          </a>
          <div className="hidden md:flex items-center gap-2">
            <MapPin size={12} strokeWidth={1.5} /><span>18 Fischer-Hallman Rd N, Unit 202, Waterloo, ON</span>
          </div>
        </div>
      </div>

      <div className="bg-[#0B0B0B] border-b border-[#C8A97E]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
          <Link to="/" aria-label="Beechwood Dental Studio home" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Beechwood Dental Studio" width="180" height="44" className="h-10 md:h-11 w-auto" />
          </Link>

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => {
              if (n.about) {
                const active = pathname === '/about' || pathname.startsWith('/about/') || pathname === '/team';
                return (
                  <div
                    key={n.to}
                    className="relative flex items-center"
                    onMouseEnter={openAbout}
                    onMouseLeave={scheduleCloseAbout}
                  >
                    <Link
                      to={n.to}
                      aria-current={active ? 'page' : undefined}
                      aria-haspopup="menu"
                      aria-expanded={aboutOpen}
                      className={`nav-link font-body text-[11px] tracking-[0.28em] uppercase inline-flex items-center gap-1.5 ${
                        active ? 'text-[#C8A97E]' : 'text-[#F0E9DC]/80 hover:text-[#C8A97E]'
                      }`}
                    >
                      {n.label}
                      <ChevronDown
                        size={10}
                        strokeWidth={1.6}
                        className={`transition-transform duration-300 ${aboutOpen ? 'rotate-180' : ''}`}
                      />
                    </Link>
                    <div
                      role="menu"
                      aria-label="About submenu"
                      className={`absolute left-1/2 -translate-x-1/2 top-full pt-5 w-[220px] transition-all duration-300 ${
                        aboutOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'
                      }`}
                    >
                      <div className="bg-[#0B0B0B] border border-[#C8A97E]/15 shadow-xl shadow-black/40">
                        {ABOUT_LINKS.map((l) => (
                          <Link
                            key={l.to}
                            to={l.to}
                            role="menuitem"
                            onClick={() => setAboutOpen(false)}
                            className={`block px-5 py-4 border-b border-[#C8A97E]/10 last:border-b-0 font-body text-[11px] tracking-[0.26em] uppercase transition-colors ${
                              pathname === l.to
                                ? 'bg-[#C8A97E]/10 text-[#C8A97E]'
                                : 'text-[#F0E9DC]/85 hover:bg-[#C8A97E]/10 hover:text-[#C8A97E]'
                            }`}
                          >
                            {l.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              if (n.children) {
                const active = pathname === n.to || pathname.startsWith('/services/');
                return (
                  <div
                    key={n.to}
                    className="relative flex items-center"
                    onMouseEnter={openServices}
                    onMouseLeave={scheduleCloseServices}
                  >
                    <Link
                      to={n.to}
                      aria-current={active ? 'page' : undefined}
                      aria-haspopup="menu"
                      aria-expanded={servicesOpen}
                      className={`nav-link font-body text-[11px] tracking-[0.28em] uppercase inline-flex items-center gap-1.5 ${
                        active ? 'text-[#C8A97E]' : 'text-[#F0E9DC]/80 hover:text-[#C8A97E]'
                      }`}
                    >
                      {n.label}
                      <ChevronDown
                        size={10}
                        strokeWidth={1.6}
                        className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`}
                      />
                    </Link>
                    <div
                      role="menu"
                      aria-label="Services submenu"
                      className={`absolute left-1/2 -translate-x-1/2 top-full pt-5 w-[280px] transition-all duration-300 ${
                        servicesOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'
                      }`}
                    >
                      <div className="bg-[#0B0B0B] border border-[#C8A97E]/15 shadow-xl shadow-black/40">
                        {services.map((s) => (
                          <Link
                            key={s.id}
                            to={`/services/${s.id}`}
                            role="menuitem"
                            onClick={() => setServicesOpen(false)}
                            className={`block px-5 py-4 border-b border-[#C8A97E]/10 last:border-b-0 transition-colors ${
                              pathname === `/services/${s.id}`
                                ? 'bg-[#C8A97E]/10 text-[#C8A97E]'
                                : 'text-[#F0E9DC]/85 hover:bg-[#C8A97E]/10 hover:text-[#C8A97E]'
                            }`}
                          >
                            <div className="font-body text-[11px] tracking-[0.26em] uppercase">
                              {s.title}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  aria-current={pathname === n.to ? 'page' : undefined}
                  className={`nav-link font-body text-[11px] tracking-[0.28em] uppercase ${
                    pathname === n.to ? 'text-[#C8A97E]' : 'text-[#F0E9DC]/80 hover:text-[#C8A97E]'
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="#book"
              onClick={(e) => { e.preventDefault(); onBookClick && onBookClick(); }}
              className="btn-primary bg-[#C8A97E] hover:bg-[#B69463] text-[#0B0B0B] rounded-none px-6 h-11 flex items-center text-[11px] tracking-[0.24em] uppercase font-medium transition-colors"
            >
              Book Online
            </a>
          </div>

          <button
            className="lg:hidden p-2 text-[#C8A97E]"
            onClick={() => setOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            <Menu size={22} />
          </button>
        </div>
      </div>

      {/* Full-screen mobile menu */}
      <div
        id="mobile-nav"
        className={`lg:hidden fixed inset-0 z-[60] bg-[#FAF7F2] transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
      >
        <div className="h-full overflow-y-auto">
          {/* Close */}
          <button
            onClick={() => setOpen(false)}
            aria-label="Close navigation menu"
            className="absolute top-5 right-5 p-2 text-[#8B7355] hover:text-[#0B0B0B] transition-colors"
          >
            <X size={26} strokeWidth={1.5} />
          </button>

          {/* Logo & wordmark */}
          <div className="pt-10 pb-8 px-6 flex flex-col items-center">
            <img
              src="/img/logo-mark-192.webp"
              loading="lazy"
              alt="Beechwood Dental Studio"
              width="96"
              height="96"
              className="w-24 h-24 object-contain mb-4"
            />
            <div className="font-display text-[#C8A97E] text-2xl md:text-3xl tracking-[0.28em] uppercase text-center">
              Beechwood
            </div>
            <div className="font-display text-[#8B7355] text-sm tracking-[0.32em] uppercase mt-1">
              Dental Studio
            </div>
          </div>

          {/* Divider */}
          <div className="mx-6 h-px bg-[#0B0B0B]/12" />

          {/* Nav list */}
          <nav aria-label="Mobile primary" className="px-6">
            <ul className="list-none p-0 m-0">
              {NAV.map((n) => {
                if (n.about) {
                  const active = pathname === '/about' || pathname.startsWith('/about/') || pathname === '/team';
                  return (
                    <li key={n.to} className="border-b border-[#0B0B0B]/10">
                      <button
                        type="button"
                        onClick={() => setMobileAboutOpen((v) => !v)}
                        aria-expanded={mobileAboutOpen}
                        aria-controls="mobile-about-submenu"
                        className={`w-full flex items-center justify-center gap-2 py-5 font-body text-[15px] tracking-[0.32em] uppercase transition-colors ${
                          active ? 'text-[#B69463]' : 'text-[#0B0B0B] hover:text-[#B69463]'
                        }`}
                      >
                        {n.label}
                        <ChevronDown
                          size={16}
                          strokeWidth={1.6}
                          className={`transition-transform duration-300 ${mobileAboutOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      <ul
                        id="mobile-about-submenu"
                        className={`list-none p-0 m-0 overflow-hidden transition-[max-height,opacity] duration-400 ${
                          mobileAboutOpen ? 'max-h-[400px] opacity-100 pb-3' : 'max-h-0 opacity-0'
                        }`}
                      >
                        {ABOUT_LINKS.map((l) => (
                          <li key={l.to}>
                            <Link
                              to={l.to}
                              onClick={() => setOpen(false)}
                              className={`block py-3 text-center text-[13px] tracking-[0.18em] transition-colors ${
                                pathname === l.to
                                  ? 'text-[#B69463]'
                                  : 'text-[#0B0B0B]/85 hover:text-[#B69463]'
                              }`}
                            >
                              {l.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                }
                if (n.children) {
                  const active = pathname === n.to || pathname.startsWith('/services/');
                  return (
                    <li key={n.to} className="border-b border-[#0B0B0B]/10">
                      <button
                        type="button"
                        onClick={() => setMobileServicesOpen((v) => !v)}
                        aria-expanded={mobileServicesOpen}
                        aria-controls="mobile-services-submenu"
                        className={`w-full flex items-center justify-center gap-2 py-5 font-body text-[15px] tracking-[0.32em] uppercase transition-colors ${
                          active ? 'text-[#B69463]' : 'text-[#0B0B0B] hover:text-[#B69463]'
                        }`}
                      >
                        {n.label}
                        <ChevronDown
                          size={16}
                          strokeWidth={1.6}
                          className={`transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      <ul
                        id="mobile-services-submenu"
                        className={`list-none p-0 m-0 overflow-hidden transition-[max-height,opacity] duration-400 ${
                          mobileServicesOpen ? 'max-h-[600px] opacity-100 pb-3' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <li>
                          <Link
                            to={n.to}
                            onClick={() => setOpen(false)}
                            className="flex items-center justify-center gap-2 py-3 text-[11px] tracking-[0.3em] uppercase text-[#8B7355] hover:text-[#B69463] transition-colors"
                          >
                            All Services
                            <ChevronRight size={12} strokeWidth={1.6} />
                          </Link>
                        </li>
                        {services.map((s) => (
                          <li key={s.id}>
                            <Link
                              to={`/services/${s.id}`}
                              onClick={() => setOpen(false)}
                              className={`block py-3 text-center text-[13px] tracking-[0.18em] transition-colors ${
                                pathname === `/services/${s.id}`
                                  ? 'text-[#B69463]'
                                  : 'text-[#0B0B0B]/85 hover:text-[#B69463]'
                              }`}
                            >
                              {s.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                }
                return (
                  <li key={n.to} className="border-b border-[#0B0B0B]/10 last:border-b-0">
                    <Link
                      to={n.to}
                      aria-current={pathname === n.to ? 'page' : undefined}
                      onClick={() => setOpen(false)}
                      className="block py-5 text-center font-body text-[15px] tracking-[0.32em] uppercase text-[#0B0B0B] hover:text-[#B69463] transition-colors"
                    >
                      {n.label}
                    </Link>
                  </li>
                );
              })}
              <li className="border-b border-[#0B0B0B]/10">
                <button
                  onClick={() => { setOpen(false); onBookClick && onBookClick(); }}
                  className="block w-full py-5 text-center font-body text-[15px] tracking-[0.32em] uppercase text-[#0B0B0B] hover:text-[#B69463] transition-colors"
                >
                  Book Online
                </button>
              </li>
            </ul>
          </nav>

          {/* Contact info */}
          <div className="px-6 pt-10 pb-14 flex flex-col items-center text-center">
            <div className="font-display text-[#C8A97E] text-2xl tracking-[0.28em] uppercase mb-6">
              Contact Us
            </div>
            <a href="tel:+15197414141" className="text-[#0B0B0B] tracking-[0.14em] py-2 hover:text-[#B69463] transition-colors">
              Ph. {clinicInfo.phone}
            </a>
            <a href={`mailto:${clinicInfo.email}`} className="text-[#0B0B0B] tracking-[0.06em] py-2 hover:text-[#B69463] transition-colors break-all">
              {clinicInfo.email}
            </a>
            <address className="not-italic text-[#0B0B0B] tracking-[0.1em] py-2 leading-relaxed">
              18 Fischer-Hallman Rd N, Unit 202
              <br />
              Waterloo, ON
            </address>
          </div>
        </div>
      </div>
    </header>
  );
}
